import { defineComponent } from 'vue';
import { QField, QIcon, QBtn, QDialog, QInput, QCard, QCardSection, QSpinner } from 'quasar';
import { QIconPicker } from '@quasar/quasar-ui-qiconpicker';
import '@quasar/quasar-ui-qiconpicker/dist/index.css';
import './formIconPicker.css';
import BaseComponent from './baseFormMixin.jsx';
import { appSettings } from '@/Shared/appSettings.js';
import utils from '@/Shared/utils.jsx';
import TranslationContainer from '../TranslationContainer.jsx';

export default defineComponent({
    name: 'sform-iconpicker',
    mixins: [BaseComponent],
    components: {
        QField,
        QIcon,
        QBtn,
        QDialog,
        QInput,
        QCard,
        QCardSection,
        QSpinner,
        QIconPicker,
        TranslationContainer,
    },
    data: () => ({
        showPicker: false,
        filter: '',
        pagination: { page: 1, itemsPerPage: 105, totalPages: 0 },
        iconSetIcons: [],
        iconsLoading: false,
    }),
    watch: {
        showPicker(open) {
            if (open && this.iconSetIcons.length === 0 && !this.iconsLoading) {
                this.iconsLoading = true;
                import('@quasar/quasar-ui-qiconpicker/src/components/icon-set/mdi-v6.js')
                    .then((m) => {
                        this.iconSetIcons = m.default.icons;
                        this.iconsLoading = false;
                    })
                    .catch(() => {
                        this.iconsLoading = false;
                    });
            }
        },
    },
    computed: {
        currentItemIcon() {
            if (!this.itemvalue) return null;
            return <QIcon name={this.itemvalue} size="sm" />;
        },
    },
    render() {
        const slots = {
            message: ({ message }) => <TranslationContainer context={this} value={message} />,
            ...(this.hintText && appSettings.DebugTranslationPrefixSetting
                ? { hint: () => <TranslationContainer context={this} value={this.hintText} /> }
                : {}),
            default: () => (
                <div class="row items-center no-wrap q-pt-xs" style={{ width: '100%', minHeight: '28px' }}>
                    <div
                        class="col cursor-pointer"
                        style={{ minHeight: '28px', display: 'flex', alignItems: 'center' }}
                        onClick={() => { this.showPicker = true; }}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        tabindex="0"
                        role="button"
                    >
                        {this.itemvalue ? (
                            <>
                                {this.currentItemIcon}
                                <span class="q-ml-sm">{this.itemvalue.replace(/^mdi-/, '')}</span>
                            </>
                        ) : (
                            <span class="text-grey-7">Select icon</span>
                        )}
                    </div>
                    {this.itemvalue && (
                        <QBtn
                            flat
                            dense
                            round
                            size="sm"
                            icon="mdi-close"
                            onClick={(e) => { e.stopPropagation(); this.sync(''); }}
                            aria-label="Clear"
                        />
                    )}
                </div>
            ),
        };

        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText} />;

        const field = (
            <QField
                class="caption q-pa-none q-ma-none sform-input-white"
                outlined
                dense
                stack-label={this.hasInputLabel}
                labelSlot={this.hasInputLabel}
                bottom-slots={this.showFieldBottomSlots}
                hint={this.inputHint}
                hide-hint
                style={{ width: '100%' }}
            >
                {slots}
            </QField>
        );

        const dialog = (
            <QDialog
                modelValue={this.showPicker}
                onUpdate:modelValue={(v) => { this.showPicker = v; }}
            >
                <QCard class="sform-iconpicker-dialog" style={{ width: '960px', maxWidth: '96vw' }}>
                    <QCardSection>
                        <QInput
                            modelValue={this.filter}
                            onUpdate:modelValue={(v) => { this.filter = v; }}
                            outlined
                            dense
                            clearable
                            placeholder="Filter icons"
                            class="q-mb-sm"
                        />
                        <div class="sform-iconpicker-picker-wrap" style={{ height: '470px', minHeight: '470px', width: '100%' }}>
                            {this.iconsLoading ? (
                                <div class="flex flex-center column" style={{ height: '100%', gap: '12px' }}>
                                    <QSpinner size="2rem" />
                                    <span>Loading icons…</span>
                                </div>
                            ) : (
                                <QIconPicker
                                    modelValue={this.itemvalue}
                                    onUpdate:modelValue={(v) => { this.sync(v); this.showPicker = false; }}
                                    icons={this.iconSetIcons}
                                    filter={this.filter}
                                    modelPagination={this.pagination}
                                    onUpdate:modelPagination={(v) => { this.pagination = v; }}
                                    vSlots={{
                                        icon: (name) => {
                                            if (!name || typeof name !== 'string') return true;
                                            const selected = name === this.itemvalue;
                                            const label = name.replace(/^mdi-/, '');
                                            return (
                                                <QBtn
                                                    flat
                                                    unelevated
                                                    class={['flex flex-center q-pa-sm icon-picker-cell', selected && 'icon-picker-cell--selected']}
                                                    style={{ width: '48px', height: '48px', minWidth: '48px', borderRadius: '8px' }}
                                                    title={label}
                                                    onClick={() => { this.sync(name); this.showPicker = false; }}
                                                >
                                                    <QIcon name={name} size="md" />
                                                </QBtn>
                                            );
                                        },
                                    }}
                                />
                            )}
                        </div>
                        <div class="row justify-end q-mt-sm">
                            <QBtn flat label="Cancel" onClick={() => { this.showPicker = false; }} />
                        </div>
                    </QCardSection>
                </QCard>
            </QDialog>
        );

        if (this.appearance)
            utils.applyVue2ToVue3Props(field, this.appearance);

        return (
            <div>
                {field}
                {dialog}
            </div>
        );
    },
});
